import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel,
} from '@trialcard/apigateway.models';
import { Gender, RoleType } from '@trialcard/enums';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Action, Mutation } from 'vuex-module-decorators';

import { EdgeEnrollmentApi } from '@trialcard/apigateway.client';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import moment, { Moment } from 'moment';
import { PDFDocument } from 'pdf-lib';
import { EdgeDocumentsApi } from '@trialcard/apigateway.client/edgeDocumentsApi';
import { string } from 'yargs';
import { Settings } from '~/services/settings';
import SurveyModule from '~/store/survey';
import genders from '~/assets/genders';
import PharmacyEligibilityModule from '~/store/pharmacy-eligibility';
import EligibilityModule from '~/store/eligibility';

@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    @Resolve
    edgeDocumentsApi!: EdgeDocumentsApi;

    @Resolve
    public hcpPharmacyeligibility!: PharmacyEligibilityModule;

    @Resolve
    public patientEligibility!: EligibilityModule;

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null,
        relationShipToPatient: '' as string,
        caregiverFirstName: '' as string,
        trainingType: '' as string,
    };

    defaultCareGiverInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        relationShipToPatient: '' as string,
    };

    defaultPrescriberData = [
        {
            model: {
                firstName: '',
                lastName: '',
                npi: '',
                accountRelationshipIsPrimary: true,
                prescriberLicenses: [],
                externalIds: [],
                addresses: [],
                phoneNumbers: [],
                emailAddresses: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[];

    defaultSiteData = [
        {
            model: {
                accountRelationshipIsPrimary: true,
                siteRoleType: RoleType.PrescriberSite,
                pointOfContacts: [],
                description: '',
                addresses: [
                    {
                        addressOne: '',
                        addressTwo: '',
                        zip: '',
                        city: '',
                        state: '',
                    },
                ],
                contacts: [],
                emailAddresses: [],
                externalIds: [],
                phoneNumbers: [],
                globalIds: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[];

    defaultPayorData = [
        {
            model: {
                payorId: (null as unknown) as number,
                payorType: (null as unknown) as number,
                accountRelationshipIsPrimary: true,
                payorGroup: '',
                payorName: '',
                webAddresses: [],
                addresses: [],
                phoneNumbers: [
                    {
                        phoneType: 1,
                        phoneNumber: '',
                        phonePurpose: 1,
                    },
                    {
                        phoneType: 3,
                        phoneNumber: '',
                        phonePurpose: 8,
                    },
                ],
                emailAddresses: [],
                contacts: [],
                payorBenefit: {
                    dateOfBirth: null as Moment | null,
                    policyHolderFirstName: '',
                    policyHolderLastName: '',
                    relationshipToPatient: undefined,
                    policyNumber: '',
                    planType: undefined,
                },
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[];
    // defaultNcpdp = '';

    defaultContactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];
    defaultContacts = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[];

    defaultMemberNumbers = {
        medical: 'XXXXXXXXXXX',
        pharmacy: 'XXXXXXXXXXX',
    };
    defaultMemberNumber = 'XXXXXXXXXXX';
    defaultExpirationDate = moment();
    pharmaMemberNumber = this.defaultMemberNumbers.pharmacy;
    medicalMemberNumber = this.defaultMemberNumbers.medical;

    public defaultAuthorizationData = {
        consentCheckbox: false as boolean,
        tncCheckbox: false as boolean,
        // fcraCheckbox: false as boolean,
        tcpaConsentCheckbox: false as boolean,
        FName: '',
        LName: '',
        repLName: '',
        repFName: '',
        patientHomePhone: '',
        relationShipToPatient: '',
    };

    public defaultPrescriberInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        siteName: '' as string,
        officeContactName: '' as string,
        addressLineOne: '' as string,
        addressLineTwo: '' as string,
        zipCode: '' as string,
        city: '' as string,
        state: '' as string,
        officeContactPrimaryPhone: '' as string,
        officePrimaryFax: '' as string,
    }

    memberNumber = this.defaultMemberNumber;
    expirationDate = this.defaultExpirationDate;
    patientInformationData = { ...this.defaultPatientInformationData };
    careGiverInformationData = { ...this.defaultCareGiverInformationData };
    prescriberData = [...this.defaultPrescriberData];
    siteData = [...this.defaultSiteData];
    payorData = [...this.defaultPayorData];
    contactPreferences = [...this.defaultContactPreferences];
    AuthorizationData = { ...this.defaultAuthorizationData };
    isCareGiver = false as boolean;
    contacts = [...this.defaultContacts] as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[];
    ncpdp = '';
    pharmacySignature = '' as string;
    selectedDrug = '' as string;
    prescriberSignature = '' as string;
    trainingType = ''as string;
    prescriberInformationData = { ...this.defaultPrescriberInformationData }

    @Mutation
    public setPharmaMemberNumber(pharmaMemberNumber: string) {
        this.pharmaMemberNumber = pharmaMemberNumber;
    }

    @Mutation
    public setMedicalMemberNumber(medicalMemberNumber: string) {
        this.medicalMemberNumber = medicalMemberNumber;
    }

    @Mutation
    updateAuthorizationData(newAuthorizationData: typeof EnrollmentModule.prototype.defaultAuthorizationData) {
        this.AuthorizationData = { ...newAuthorizationData };
    }

    @Mutation
    public async updateContacts(newContacts: EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[]) {
        this.contacts = newContacts;
    }

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    public async updatePrescribers(newPrescriberData: UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[]) {
        this.prescriberData = [...newPrescriberData];
    }

    @Mutation
    public async updateSites(newSites: UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[]) {
        this.siteData = [...newSites];
    }

    @Mutation
    public async updatePayors(newPayorData: UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[]) {
        this.payorData = [...newPayorData];
    }

    @Mutation
    public async updateCareGiverInformation(newCareGiverInformation: typeof EnrollmentModule.prototype.careGiverInformationData) {
        this.careGiverInformationData = { ...this.patientInformationData, ...newCareGiverInformation };
    }

    @Mutation
    public async updateContactPreferences(newContactPreferences: EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[]) {
        this.contactPreferences = [...newContactPreferences];
    }

    @Mutation
    public setMemberNumber(memberNumber: string) {
        this.memberNumber = memberNumber;
    }

    @Mutation
    public setNcpdp(ncpdp: string) {
        this.ncpdp = ncpdp;
    }

    @Mutation
    public setPharmacySignature(pharmacySignature: string) {
        this.pharmacySignature = pharmacySignature;
    }
    @Mutation
    public setSelectedDrug(selectedDrug: string) {
        this.selectedDrug = selectedDrug;
    }

    @Mutation
    public setPrescriberSignature(prescriberSignature: string) {
        this.prescriberSignature = prescriberSignature;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }

    @Mutation
    public setIsCareGiver(isCareGiver: boolean) {
        this.isCareGiver = isCareGiver;
    }

    @Mutation
    public setTrainingType(trainingType: string) {
        this.patientInformationData.trainingType = trainingType;
    }

    @Mutation
    public setPrescriberInformationData(newPrescriberInformationData: typeof EnrollmentModule.prototype.prescriberInformationData) {
        this.prescriberInformationData = newPrescriberInformationData
    }

    @Action({ rawError: true })
    public clearStore() {
        this.setMemberNumber(this.defaultMemberNumber);
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
        this.setExpirationDate(this.defaultExpirationDate);
        this.updatePatientInformation(this.defaultPatientInformationData);
        this.updateCareGiverInformation(this.defaultCareGiverInformationData);
        this.updatePrescribers(this.defaultPrescriberData);
        this.updateSites(this.defaultSiteData);
        this.updatePayors(this.defaultPayorData);
        this.updateContactPreferences(this.defaultContactPreferences);
        this.updateAuthorizationData(this.defaultAuthorizationData);
        this.setIsCareGiver(false);
        this.hcpPharmacyeligibility.clearStore();
        this.patientEligibility.clearStore();
        this.setNcpdp('');
        this.updateContacts(this.defaultContacts);
        this.setPharmacySignature('');
        this.setSelectedDrug('');
        this.setPrescriberSignature('');
        this.setTrainingType('');
        this.setPrescriberInformationData(this.defaultPrescriberInformationData);
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setMemberNumber(this.defaultMemberNumber);
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
        this.updateAuthorizationData(this.defaultAuthorizationData);
        this.setNcpdp('');
        this.setPharmacySignature('');
        this.setSelectedDrug('');
        this.setPrescriberSignature('');
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        let response = null;
        const { prescriberData, siteData, payorData, contactPreferences } = this;
        try {
            const patient = {
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            contactPreferences,
                            // contacts: this.contacts,
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                // accountAttestations: [
                //     {
                //         code: 'eServiceAttestion',
                //         startDate: moment(),
                //     },
                // ],
            } as EnrollPatientRequestModel;

            const axios: AxiosInstance = (this.patientEnrollmentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: '/edge/patientEnrollment/enrollPatient',
                method: 'POST',
                data: patient,
                headers: {
                    'x-program-id': this.settings.programId,
                },
            };
            await Promise.all([
                (this.patientEnrollmentApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
                (this.patientEnrollmentApi as any).authentications.Bearer.applyToRequest(axiosRequest),
                (this.patientEnrollmentApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            response = await axios.request(axiosRequest);
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }

    public async uploadPdf(blob: Blob) {
        const formData = new FormData();
        formData.append('type', '10001');
        formData.append('attachment', blob);

        formData.append('externalPartyCode', 'CLS');
        formData.append('externalProgramCode', 'HYRI');
        const axios: AxiosInstance = (this.edgeDocumentsApi as any).axios;
        const axiosRequest: AxiosRequestConfig = {
            url: 'edge/documents/v1/Document/upload',
            method: 'POST',
            data: formData,
            headers: {
                'x-program-id': this.settings.clsProgramId!,
            },
        };

        await Promise.all([
            // (this.edgeDocumentsApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
            // (this.edgeDocumentsApi as any).authentications.Bearer.applyToRequest(axiosRequest),
            (this.edgeDocumentsApi as any).authentications.Token.applyToRequest(axiosRequest),
        ]);

        const result = await axios.request(axiosRequest);
        return result;
    }
}
